import { Card, Flex, SimpleTable, Textarea, Typography } from "djuno-design";
import { ReactComponent as FaceSmile } from "./../../assets/icons/face-smile.svg";
import ExpandableCard from "../../components/ExpandableCard";
import { GithubTypesLink } from "./../../urls";

const { Title, Text } = Typography;

const TextareaPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Textarea</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Textarea component in Djuno Design provides a customizable
            multi-line text input for capturing larger blocks of user input,
            such as comments, descriptions, or messages. Like other form
            elements, the Textarea supports labeling, error handling, tooltips,
            hints, loading states, and additional functionality such as
            clipboard copying. Its flexible API makes it an ideal choice for
            applications where users need to enter detailed text content.
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Textarea component offers several customization options:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Label and Required Field
                </Text>
                : Displays a label above the textarea and indicates if the field
                is required with a * symbol.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Error Handling
                </Text>
                : Displays animated error messages beneath the textarea when
                validation fails.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Hint and Tooltip
                </Text>
                : You can provide contextual help via hints and tooltips to
                guide users in completing the textarea.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Copy-to-Clipboard
                </Text>
                : The Textarea supports copying its content to the clipboard,
                with customizable copy behavior.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Error State Styling
                </Text>
                : The textarea automatically changes its appearance when an
                error is present, providing visual feedback to the user.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Loading States
                </Text>
                : When the textarea is loading, a visual indicator (spinner)
                appears, signaling to users that the field is being processed.
              </Text>
            </li>
          </ul>

          <Title level={5} className="mt-4">
            Additional Features
          </Title>
          <Text size="sm">
            The Textarea component comes with the following additional features:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Copyable Content
                </Text>
                : When enabled, the Textarea allows users to copy its contents
                to the clipboard with a single click. This can be controlled via
                the copyable prop, which can be a function for custom formatting
                before copying.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Dynamic Error Handling
                </Text>
                : When an error occurs (e.g., required field not filled), an
                animated error message appears underneath the textarea, making
                it highly responsive.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Tooltips for Additional Guidance
                </Text>
                : Tooltips can be added to provide more detailed information for
                the user, helping them understand the purpose or requirements of
                the field.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Responsive Design
                </Text>
                : The Textarea adjusts its size and appearance to fit within
                various form layouts, with a range of size options.
              </Text>
            </li>
          </ul>
        </Flex>

        {/* Examples */}
        <Flex direction="col" className="w-full">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4 w-full">
            <ExpandableCard
              codeViewerProps={{
                code: TextareaExample1,
              }}
            >
              <Text size="sm" className="font-semibold ">
                Have hit:
              </Text>
              <Flex direction="col" className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  hint="Djuno Design"
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Simple form:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Have loading:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  loading
                  loadingType="elastic"
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Replace copy text.:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder=""
                  tooltip={{ content: "test" }}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Have error with error text:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  required
                  error="field is required!"
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Copyable without function:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  copyable={true}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Copyable with function:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  copyable={{
                    text: ({ value }) => `Hi ${value}`,
                  }}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Have error without error text:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  error={true}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Custom Copy icon and replace tooltips text:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  copyable={{
                    icons: [
                      <FaceSmile />,
                      <FaceSmile className="text-green-500" />,
                    ],
                    tooltips: ["Click to copy", "Text copied!"],
                  }}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Hide Copy tooltips:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  copyable={{
                    icons: [
                      <FaceSmile />,
                      <FaceSmile className="text-green-500" />,
                    ],
                    tooltips: false,
                  }}
                />
              </Flex>
              <Text size="sm" className="font-semibold mt-10">
                Custom label:
              </Text>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  labelClassName="text-green-500 font-bold"
                />
              </Flex>
              <Flex className="gap-5 w-full mt-5">
                <Textarea
                  error
                  label="Textarea"
                  placeholder="Enter custom notes if any"
                  labelClassName="text-green-500 font-bold"
                />
              </Flex>
            </ExpandableCard>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* id */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">id</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Unique identifier</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* label */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">label</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Label displayed above the textarea.</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* loading */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loading</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        If true, displays a loading indicator within the input
                        field.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* loadingType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">loadingType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        The type of loading indicator to display.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>

                  {/* type */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Defines the visual style of the Textarea, such as
                        borders and background.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Input.ts#L44"}
                        size="xs"
                        code
                      >
                        InputTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">default</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* placeholder */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">placeholder</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Placeholder text displayed inside the textarea when it
                        is empty.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* required */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">required</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Marks the field as required, adding a * symbol next to
                        the label.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        boolean
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">false</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* error */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">error</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Error message displayed beneath the Textarea when
                        validation fails.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        {`string | boolean | React.ReactNode`}
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* hint */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">hint</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Additional contextual information displayed next to the
                        label, often used to guide the user.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        string | React.ReactNode
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm"></Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* tooltip */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">tooltip</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Tooltip configuration for providing more detailed
                        guidance or explanations.
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Tooltip.ts#L37"}
                        size="xs"
                        code
                      >
                        TooltipProps
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD></SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Controls the size of the Textarea (height and padding).
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "index.ts#L55"}
                        size="xs"
                        code
                      >
                        SizeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">medium</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
                {/* copyable */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">copyable</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Allows the textarea content to be copied to the clipboard.
                      Can be set to a boolean or a function that formats the
                      copied value.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Typography.Link
                      target="_blank"
                      href={GithubTypesLink + "index.ts#L67"}
                      size="xs"
                      code
                    >
                      {`CopyableProp`}
                    </Typography.Link>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* className */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">className</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes for customizing the overall
                      textarea container.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* labelClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">labelClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional CSS classes to apply custom styles to the
                      input's label.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
                {/* containerClassName */}
                <SimpleTable.Row>
                  <SimpleTable.TD>
                    <Text size="sm">containerClassName</Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="sm">
                      Additional classes to apply to the input container.
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD>
                    <Text size="xs" code>
                      {`string`}
                    </Text>
                  </SimpleTable.TD>
                  <SimpleTable.TD></SimpleTable.TD>
                </SimpleTable.Row>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const TextareaExample1 = `<Flex direction="col" className="gap-4 w-full">
  <ExpandableCard
    codeViewerProps={{
      code: TextareaExample1,
      
    }}
  >
    <Text size="sm" className="font-semibold ">
      Have hit:
    </Text>
    <Flex direction="col" className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        hint="Djuno Design"
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Simple form:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Have loading:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        loading
        loadingType="elastic"
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Replace copy text.:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder=""
        tooltip={{ content: "test" }}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Have error with error text:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        required
        error="field is required!"
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Copyable without function:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={true}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Copyable with function:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
            copyable={(v) => \`Hi \${v}\`}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Have error without error text:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        error={true}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Custom Copy icon and replace tooltips text:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={{
          icon: [
            <FaceSmile />,
            <FaceSmile className="text-green-500" />,
          ],
          tooltips: ["Click to copy", "Text copied!"],
        }}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Hide Copy tooltips:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        copyable={{
          icon: [
            <FaceSmile />,
            <FaceSmile className="text-green-500" />,
          ],
          tooltips: false,
        }}
      />
    </Flex>
    <Text size="sm" className="font-semibold mt-10">
      Custom label:
    </Text>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        label="Textarea"
        placeholder="Enter custom notes if any"
        labelClassName="text-green-500 font-bold"
      />
    </Flex>
    <Flex className="gap-5 w-full mt-5">
      <Textarea
        error
        label="Textarea"
        placeholder="Enter custom notes if any"
        labelClassName="text-green-500 font-bold"
      />
    </Flex>
  </ExpandableCard>
</Flex>`;

export default TextareaPage;
